import { useEffect, useState } from "react";
import {
  getAppstoreAnalytics,
  getGenreViews,
  getMostViewedVideos,
  getPlaystoreAnalytics,
  getStoreDownloads,
} from "../../../../services/analytics";
import {
  getUserListCount,
  getUsersActivesCount,
  getUsersFreeCount,
  getUsersActivityCounts,
  getActiveUsersEvolution,
} from "../../../../services/users";
import { changeOf } from "../../../../utils/numbers";

/**
 * Users split by app activity: active (seen over the last 30 days), inactive (seen before)
 * and never seen. `null` when the counters are unavailable.
 */
export function activityBreakdown(total, counts) {
  if (!Number.isFinite(total) || !Number.isFinite(counts?.active)) return null;
  const everActive = Number.isFinite(counts.everActive)
    ? Math.max(counts.everActive, counts.active)
    : counts.active;
  return {
    active: counts.active,
    inactive: everActive - counts.active,
    neverSeen: Math.max(total - everActive, 0),
  };
}

/** User counters: subscription, account status (`status`) and app activity (`lastActiveAt`). */
export function useUserCounts() {
  const [counts, setCounts] = useState({
    loading: true,
    total: [],
    free: [],
    paid: [],
    enabled: [],
    disabled: [],
    activity: null,
  });

  useEffect(() => {
    let isMounted = true;
    async function fetchCounts() {
      // In parallel: the four counters do not depend on each other.
      // `enabled`: account status set by an admin (`status: true`), not app activity
      const [total, free, enabled, activity] = await Promise.all([
        getUserListCount(),
        getUsersFreeCount(),
        getUsersActivesCount(),
        getUsersActivityCounts(),
      ]);
      if (!isMounted) return;
      setCounts({
        loading: false,
        total,
        free,
        paid: total - free,
        enabled,
        disabled: total - enabled,
        activity: activityBreakdown(total, activity),
      });
    }
    fetchCounts();
    return () => {
      isMounted = false;
    };
  }, []);

  return counts;
}

/** Active users over the last `days` days vs the period before (see getActiveUsersEvolution). */
export function useActiveUsersEvolution(days) {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let cancelled = false;
    setState((previous) => ({ ...previous, loading: true }));
    getActiveUsersEvolution(days).then((data) => {
      if (!cancelled) setState({ loading: false, data });
    });
    return () => {
      cancelled = true;
    };
  }, [days]);

  return state;
}

const toCount = (value) =>
  value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
    ? null
    : Number(value);

/** Normalized store stats: every missing figure is `null`. */
export function normalizeStoreStats(data) {
  if (!data) return null;
  const rating = toCount(data.rating);
  return {
    tier: data.downloads || null,
    downloads: toCount(data.downloadsCount),
    downloadsLast30Days: toCount(data.downloadsLast30Days),
    activeDevices: toCount(data.activeDevicesCount),
    // Same length period just before, to show the evolution
    activeDevicesPrevious: toCount(data.activeDevicesPreviousCount),
    rating: rating > 0 ? rating : null,
    reviews: toCount(data.reviewsCount),
  };
}

const sumOf = (values) => {
  const known = values.filter((value) => value !== null);
  return known.length ? known.reduce((sum, value) => sum + value, 0) : null;
};

/**
 * Evolution in % of the active devices of `stores` vs the previous period, `null` unless
 * every store counted now also has its previous figure (otherwise the sums are not comparable).
 */
export function activeDevicesChange(stores) {
  const counted = stores.filter((store) => store && store.activeDevices !== null);
  if (!counted.length || counted.some((store) => store.activeDevicesPrevious === null)) {
    return null;
  }
  return changeOf(
    sumOf(counted.map((store) => store.activeDevices)),
    sumOf(counted.map((store) => store.activeDevicesPrevious))
  );
}

/** iOS + Android totals; the rating is averaged, weighted by the reviews of each store. */
export function combineStoreStats(stores) {
  const available = stores.filter(Boolean);
  const rated = available.filter((store) => store.rating && store.reviews);
  const ratedReviews = sumOf(rated.map((store) => store.reviews));
  return {
    downloads: sumOf(available.map((store) => store.downloads)),
    downloadsLast30Days: sumOf(
      available.map((store) => store.downloadsLast30Days)
    ),
    activeDevices: sumOf(available.map((store) => store.activeDevices)),
    activeDevicesChange: activeDevicesChange(available),
    reviews: sumOf(available.map((store) => store.reviews)),
    rating: ratedReviews
      ? rated.reduce((sum, store) => sum + store.rating * store.reviews, 0) /
        ratedReviews
      : null,
  };
}

/** App Store and Play Store stats, loaded in parallel (`null` for a store that failed). */
export function useStoreStats() {
  const [stats, setStats] = useState({
    loading: true,
    ios: null,
    android: null,
  });

  useEffect(() => {
    let isMounted = true;
    Promise.allSettled([getAppstoreAnalytics(), getPlaystoreAnalytics()]).then(
      ([ios, android]) => {
        if (!isMounted) return;
        setStats({
          loading: false,
          ios: ios.status === "fulfilled" ? normalizeStoreStats(ios.value) : null,
          android:
            android.status === "fulfilled"
              ? normalizeStoreStats(android.value)
              : null,
        });
      }
    );
    return () => {
      isMounted = false;
    };
  }, []);

  return stats;
}

/**
 * Data loaded by `load(...args)`, reloaded when `args` change (compared by value).
 * `null` args (e.g. an invalid date range) keep the last result.
 */
function useViewsData(load, args) {
  const [state, setState] = useState({ loading: true, data: null });
  const key = args ? JSON.stringify(args) : null;

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    setState((previous) => ({ ...previous, loading: true }));
    load(...JSON.parse(key)).then((data) => {
      if (!cancelled) setState({ loading: false, data });
    });
    return () => {
      cancelled = true;
    };
  }, [load, key]);

  return state;
}

/** The 5 most viewed videos for a period filter (see getMostViewedVideos). */
export function useMostViewedVideos(filter) {
  return useViewsData(getMostViewedVideos, filter && [filter]);
}

/** Views of every genre of `category` for a period filter (see getGenreViews). */
export function useGenreViews(category, filter) {
  return useViewsData(getGenreViews, filter && [category, filter]);
}

/** Store downloads for `range` (7, 30 or "all"), see getStoreDownloads. */
export function useStoreDownloads(range) {
  return useViewsData(getStoreDownloads, [range]);
}
